import React from 'react';
import { motion } from 'framer-motion';
import { Hammer, Lightbulb, Mic, Calendar, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';

const bookingOptions = [
  {
    id: 'build-session',
    title: 'Book a Build Session',
    description: 'Roll up your sleeves and build something together. Whether it\'s architecting an AI solution, prototyping a product, or solving a technical challenge — let\'s create.',
    icon: Hammer,
    color: 'from-teal to-teal-dark',
    bgColor: 'bg-teal/10',
    textColor: 'text-teal',
  },
  {
    id: 'discovery-workshop',
    title: 'Book a Discovery Workshop',
    description: 'Explore the possibilities. We\'ll dive deep into your organization\'s AI potential, identify opportunities, and map out a transformation strategy tailored to your goals.',
    icon: Lightbulb,
    color: 'from-orange to-orange-dark',
    bgColor: 'bg-orange/10',
    textColor: 'text-orange',
  },
  {
    id: 'interview',
    title: 'Book an Interview',
    description: 'Podcast, panel, keynote, or media — I love sharing insights on AI transformation, the human+AI future, and building technology that empowers people.',
    icon: Mic,
    color: 'from-blue to-blue-dark',
    bgColor: 'bg-blue/10',
    textColor: 'text-blue',
  },
];

const CALENDLY_LINK = 'https://calendly.com/jsethcushing/30min';

export function BookingSection() {
  return (
    <section
      id="booking"
      data-testid="booking-section"
      className="py-20 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-teal font-mono text-sm uppercase tracking-widest mb-4">
            Let's Connect
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold mb-6">
            Ready to Build the Future?
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Whether you're looking to transform your organization, build something new, 
            or share ideas — I'm here to help. Choose how you'd like to connect.
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-teal via-orange to-blue mx-auto rounded-full mt-8" />
        </motion.div>

        {/* Booking Cards */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {bookingOptions.map((option, index) => {
            const Icon = option.icon;
            return (
              <motion.div
                key={option.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="h-full"
              >
                <Card 
                  className="glass-light border-0 h-full hover:shadow-2xl transition-all duration-500 group overflow-hidden relative"
                  data-testid={`booking-card-${option.id}`}
                >
                  {/* Gradient overlay on hover */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${option.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`} />
                  
                  <CardContent className="p-8 flex flex-col h-full relative z-10">
                    {/* Icon */}
                    <div className={`w-16 h-16 rounded-2xl ${option.bgColor} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                      <Icon className={`w-8 h-8 ${option.textColor}`} strokeWidth={1.5} />
                    </div>

                    {/* Content */}
                    <h3 className="text-xl font-heading font-semibold mb-3">
                      {option.title}
                    </h3>
                    <p className="text-muted-foreground flex-grow mb-6">
                      {option.description}
                    </p>

                    {/* CTA Button */}
                    <Button 
                      className={`w-full bg-gradient-to-r ${option.color} hover:opacity-90 text-white group/btn`}
                      asChild
                      data-testid={`booking-btn-${option.id}`}
                    >
                      <a href={CALENDLY_LINK} target="_blank" rel="noopener noreferrer">
                        <Calendar className="w-4 h-4 mr-2" />
                        Schedule Now
                        <ArrowRight className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom tagline */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center text-muted-foreground mt-12 text-sm"
        >
          All sessions are 30 minutes. Longer engagements can be discussed during our call.
        </motion.p>
      </div>
    </section>
  );
}
